import Web3Singleton from 'web3Singleton';

interface OfferWithSignatureHash {
  signature_hash: string;
  [key: string]: any;
}

/**
 * Randomly selects an unused offer from the available offers array.
 * Checks each randomly selected offer against the contract to see if the signature has been used.
 * If used, removes it from the pool and tries again with another random selection.
 *
 * @param offers - Array of offers that have a signature_hash field
 * @returns The first randomly selected unused offer, or null if all offers are used
 */
export const selectRandomUnusedOffer = async <T extends OfferWithSignatureHash>(offers: T[]): Promise<T | null> => {
  if (!offers || offers.length === 0) {
    return null;
  }

  const lgoWeb3 = Web3Singleton.getInstance();
  let availableOffers = [...offers];
  let selectedOffer: T | null = null;

  // Keep trying random offers until we find an unused one or run out
  while (availableOffers.length > 0 && !selectedOffer) {
    // Pick a random offer from the remaining available offers
    const randomIndex = Math.floor(Math.random() * availableOffers.length);
    const randomOffer = availableOffers[randomIndex];

    try {
      // Check if this signature has been used on the contract
      const isUsed = await lgoWeb3.isSignatureUsed(randomOffer.signature_hash);

      if (!isUsed) {
        selectedOffer = randomOffer;
      } else {
        // Remove this used offer from the available list and try again
        availableOffers.splice(randomIndex, 1);
      }
    } catch (error) {
      console.warn('Error checking signature status, removing offer from pool:', error);
      // Remove this offer from the pool if we can't verify it
      availableOffers.splice(randomIndex, 1);
    }
  }

  return selectedOffer;
};
